import CheckCircle from "@mui/icons-material/CheckCircle";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import {
    Box,
    Button,
    Card,
    CardContent,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Typography,
} from "@mui/material";
import { usePostHog } from "@posthog/react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthInfo } from "../../allauth/auth/hooks";
import ApiClient from "../../config/ApiClient";
import { useAppContext } from "../../context/AppContext";
import { useOnboarding } from "../../context/OnboardingContext";
import { useRoleLabels } from "../../hooks/useRoleLabels";
import type { AthletePaymentAssignment } from "../../object-actions/types/types";
import {
    assignmentHasCoachingSupport,
    assignmentOnboardingPath,
    assignmentWaiverComplete,
    computeOnboardingSteps,
    type OnboardingStep,
} from "../../utils/onboarding";
import CoachingStepActions from "./CoachingStepActions";

interface Props {
    variant?: "compact" | "full";
    onAction?: (step: OnboardingStep) => void;
    title?: string;
    /** When shown outside the scoped onboarding route (e.g. dashboard). */
    assignmentId?: number | null;
    startingAi?: boolean;
    onInviteSupport?: () => void;
    onStartWithAi?: () => void;
}

const OnboardingChecklist: React.FC<Props> = ({
    variant = "full",
    onAction,
    title,
    assignmentId: assignmentIdProp,
    startingAi = false,
    onInviteSupport,
    onStartWithAi,
}) => {
    const navigate = useNavigate();
    const posthog = usePostHog();
    const { user } = useAuthInfo();
    const { organization } = useAppContext();
    const { applyRoleLabels } = useRoleLabels();

    const contextOnboarding = useOnboarding();

    const [fetchedAssignment, setFetchedAssignment] = useState<AthletePaymentAssignment | null>(null);
    const [fetchLoading, setFetchLoading] = useState(false);

    const effectiveAssignmentId =
        assignmentIdProp ?? contextOnboarding?.assignmentId ?? null;

    const useContextData =
        contextOnboarding &&
        effectiveAssignmentId != null &&
        contextOnboarding.assignmentId === effectiveAssignmentId;

    useEffect(() => {
        if (useContextData || !effectiveAssignmentId || variant !== "compact") {
            setFetchedAssignment(null);
            return;
        }
        let cancelled = false;
        setFetchLoading(true);
        ApiClient.get<AthletePaymentAssignment>(
            `/api/athlete-assignments-by-id/${effectiveAssignmentId}/`,
        )
            .then((res) => {
                if (!cancelled && res.success && res.data) {
                    setFetchedAssignment(res.data);
                }
            })
            .finally(() => {
                if (!cancelled) setFetchLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [useContextData, effectiveAssignmentId, variant]);

    const derived = useMemo(() => {
        if (useContextData && contextOnboarding) {
            return {
                steps: contextOnboarding.steps,
                percent: contextOnboarding.percent,
                isComplete: contextOnboarding.isComplete,
                currentStep: contextOnboarding.currentStep,
                loading: contextOnboarding.loading,
                withReturn: contextOnboarding.withReturn,
                assignment: contextOnboarding.assignment,
            };
        }
        const assignment = fetchedAssignment;
        const state = computeOnboardingSteps({
            user,
            assignment,
            assignmentId: effectiveAssignmentId,
            orgWaiver: organization
                ? {
                      waiver_athlete: organization.waiver_athlete ?? organization.waiver_name,
                      waiver_adult: organization.waiver_adult,
                  }
                : null,
            applyRoleLabels,
            hasPendingSupportInvite: contextOnboarding?.hasPendingSupportInvite,
        });
        return {
            ...state,
            loading: fetchLoading,
            withReturn: (path: string) => path,
            assignment,
        };
    }, [
        useContextData,
        contextOnboarding,
        fetchedAssignment,
        user,
        effectiveAssignmentId,
        organization,
        applyRoleLabels,
        fetchLoading,
    ]);

    const { steps, percent, isComplete, currentStep, loading, withReturn, assignment } = derived;

    const applicableSteps = steps.filter((s) => s.applicable);
    if (applicableSteps.length === 0 || !effectiveAssignmentId) return null;

    const orgWaiver = organization
        ? {
              waiver_athlete: organization.waiver_athlete ?? organization.waiver_name,
              waiver_adult: organization.waiver_adult,
          }
        : null;
    const waiverDone = assignmentWaiverComplete(
        assignment,
        user,
        assignment?.my_roles,
        orgWaiver,
    );
    const coachingReady = assignmentHasCoachingSupport(assignment);

    const handleStepCta = (step: OnboardingStep) => {
        posthog?.capture("onboarding_step_cta_clicked", {
            step: step.key,
            cta_label: step.cta?.label,
            cta_action: step.cta?.action || "navigate",
            variant,
            percent,
            assignment_id: effectiveAssignmentId,
        });
        if (step.cta?.to) {
            navigate(withReturn(step.cta.to));
        } else if (step.cta?.action) {
            onAction?.(step);
        }
    };

    const handleContinue = () => {
        posthog?.capture("onboarding_continue_clicked", {
            variant,
            percent,
            next_step: currentStep?.key,
            assignment_id: effectiveAssignmentId,
        });
        navigate(withReturn(assignmentOnboardingPath(effectiveAssignmentId)));
    };

    if (variant === "compact") {
        if (isComplete || loading) return null;
        return (
            <Card sx={{ mb: 2 }} data-testid="onboarding-checklist-compact">
                <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {title || "Finish setting up your account"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {percent}%
                        </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={percent} sx={{ mb: 1.5, borderRadius: 1 }} />
                    {currentStep && (
                        <Typography variant="body2" color="text.secondary" mb={1.5}>
                            Next: {currentStep.label}
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={handleContinue}
                        data-attr="onboarding_continue"
                        data-testid="onboarding-continue"
                    >
                        Continue setup
                    </Button>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box data-testid="onboarding-checklist-full">
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1}>
                <Typography variant="h6" fontWeight={600}>
                    {title || "Your setup checklist"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {percent}% complete
                </Typography>
            </Box>
            <LinearProgress variant="determinate" value={percent} sx={{ mb: 2, borderRadius: 1 }} />
            <List disablePadding>
                {applicableSteps.map((step) => {
                    const isCurrent = currentStep?.key === step.key;
                    const strikeThrough =
                        step.done && !step.keepInteractiveWhenDone;

                    let secondaryAction: React.ReactNode = null;
                    if (step.key === "coaching" && onInviteSupport && onStartWithAi) {
                        secondaryAction = (
                            <CoachingStepActions
                                isCurrent={isCurrent}
                                waiverDone={waiverDone}
                                coachingReady={coachingReady}
                                startingAi={startingAi}
                                onInvite={() => {
                                    posthog?.capture("onboarding_step_cta_clicked", {
                                        step: "coaching",
                                        cta_action: "open_invite_support",
                                        variant,
                                        percent,
                                        assignment_id: effectiveAssignmentId,
                                    });
                                    onInviteSupport();
                                }}
                                onStartWithAi={() => {
                                    posthog?.capture("onboarding_step_cta_clicked", {
                                        step: "coaching",
                                        cta_action: "start_with_ai",
                                        variant,
                                        percent,
                                        assignment_id: effectiveAssignmentId,
                                    });
                                    onStartWithAi();
                                }}
                            />
                        );
                    } else {
                        const showCta =
                            !!step.cta && (!step.done || !!step.keepInteractiveWhenDone);
                        if (showCta) {
                            secondaryAction = (
                                <Button
                                    variant={isCurrent ? "contained" : "outlined"}
                                    color="secondary"
                                    size="small"
                                    disabled={!!step.cta?.disabled}
                                    onClick={() => handleStepCta(step)}
                                    data-attr={`onboarding_step_${step.key}`}
                                    data-custom-id={step.key}
                                    data-testid={`onboarding-step-cta--${step.key}`}
                                >
                                    {step.cta!.label}
                                </Button>
                            );
                        }
                    }

                    return (
                        <ListItem
                            key={step.key}
                            divider
                            data-testid={`onboarding-step--${step.key}`}
                            sx={{
                                flexWrap: "wrap",
                                alignItems:
                                    step.key === "coaching" && !coachingReady ? "flex-start" : "center",
                                py: step.key === "coaching" && !coachingReady ? 1.5 : undefined,
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40, mt: step.key === "coaching" && !coachingReady ? 0.5 : 0 }}>
                                {step.done ? (
                                    <CheckCircle color="success" />
                                ) : (
                                    <RadioButtonUnchecked color={isCurrent ? "secondary" : "disabled"} />
                                )}
                            </ListItemIcon>
                            <ListItemText
                                primary={step.label}
                                secondary={step.description}
                                primaryTypographyProps={{
                                    fontWeight: isCurrent ? 600 : 400,
                                    sx: { textDecoration: strikeThrough ? "line-through" : "none" },
                                }}
                                sx={{
                                    flex: "1 1 0",
                                    minWidth: 0,
                                    pr: secondaryAction ? { xs: 0, sm: 2 } : undefined,
                                }}
                            />
                            {secondaryAction && (
                                <Box
                                    sx={{
                                        flex: { xs: "1 1 100%", sm: "0 0 auto" },
                                        pl: { xs: 5, sm: 0 },
                                        mt: { xs: 1, sm: 0 },
                                        display: "flex",
                                        justifyContent: { xs: "flex-start", sm: "flex-end" },
                                    }}
                                >
                                    {secondaryAction}
                                </Box>
                            )}
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );
};

export default OnboardingChecklist;
